// /teams/:slug — public team profile page.
//
// Hero: logo + name + country + league badges. Roster grid reusing
// PlayerCard. Empty roster → tasteful empty state, not a crash.

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import PlayerCard, { type PlayerSearchResult } from '../components/PlayerCard'
import Avatar from '../components/Avatar'
import type { PlayerPosition } from '../types'

interface TeamLeague {
  league_name: string
  season: string
}

interface RosterPlayer {
  id: string
  slug: string
  name: string
  position: PlayerPosition | null
  nationality: string | null
  photo_url: string | null
}

interface TeamResponse {
  team: {
    id: string
    name: string
    slug: string
    country: string | null
    country_code: string | null
    logo_url: string | null
    is_national: boolean
  }
  leagues: TeamLeague[]
  roster: RosterPlayer[]
}

export default function TeamProfile() {
  const { slug } = useParams()
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'ready'; data: TeamResponse }
  >({ kind: 'loading' })

  useEffect(() => {
    let cancelled = false
    if (!slug) {
      setState({ kind: 'error', message: 'no_slug' })
      return
    }
    async function load() {
      try {
        const res = await fetch(`/api/teams/${encodeURIComponent(slug!)}`)
        if (cancelled) return
        if (res.status === 404) {
          setState({ kind: 'error', message: 'Team not found.' })
          return
        }
        if (!res.ok) {
          setState({ kind: 'error', message: `Request failed (${res.status})` })
          return
        }
        const data = (await res.json()) as TeamResponse
        if (cancelled) return
        setState({ kind: 'ready', data })
      } catch (e) {
        if (cancelled) return
        setState({
          kind: 'error',
          message: e instanceof Error ? e.message : 'network_error',
        })
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [slug])

  if (state.kind === 'loading') {
    return (
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="animate-pulse space-y-6">
          <div className="h-32 bg-hoop-surface rounded-2xl" />
          <div className="h-64 bg-hoop-surface rounded-2xl" />
        </div>
      </div>
    )
  }

  if (state.kind === 'error') {
    return (
      <div className="max-w-6xl mx-auto px-6 py-10">
        <p className="text-hoop-text-2">{state.message}</p>
      </div>
    )
  }

  const { team, leagues, roster } = state.data

  // Convert roster players to PlayerSearchResult shape for PlayerCard
  // (PlayerCard expects the full search-result shape with created_at,
  // updated_at, age, height_cm, is_verified, source, agent_id — we
  // stub missing fields so the card renders without crashing)
  const rosterAsSearchResults: PlayerSearchResult[] = roster.map((p) => ({
    id: p.id,
    slug: p.slug,
    name: p.name,
    photo_url: p.photo_url,
    position: p.position,
    nationality: p.nationality,
    height_cm: null,
    age: null,
    is_verified: false,
    created_at: '',
    updated_at: '',
    source: 'api_sports',
    agent_id: null,
    availability_status: 'evaluating' as const,
    current_team: team.name,
    headline_stats: null,
  }))

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 space-y-6">
      {/* Hero: logo + name + country + league badges */}
      <section className="bg-hoop-surface border border-hoop-border rounded-2xl p-6">
        <div className="flex items-start gap-6">
          {/* Logo with onError fallback — api-sports asset URLs often 404 */}
          <div className="flex-shrink-0">
            {team.logo_url ? (
              <img
                src={team.logo_url}
                alt={`${team.name} logo`}
                onError={(e) => {
                  // Fallback to an Avatar tile when the logo 404s
                  e.currentTarget.style.display = 'none'
                  const parent = e.currentTarget.parentElement
                  if (parent) {
                    const fallback = document.createElement('div')
                    fallback.className =
                      'w-24 h-24 rounded-lg bg-hoop-bg flex items-center justify-center text-2xl font-bold text-hoop-text'
                    fallback.textContent = team.name
                      .split(' ')
                      .map((w) => w[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)
                    parent.appendChild(fallback)
                  }
                }}
                className="w-24 h-24 object-contain rounded-lg"
              />
            ) : (
              <Avatar
                src={null}
                name={team.name}
                className="w-24 h-24 rounded-lg"
                textClassName="text-2xl"
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-display font-bold text-hoop-text mb-2">
              {team.name}
            </h1>
            {team.country && (
              <p className="text-hoop-text-2 mb-3">
                {team.country}
                {team.is_national && (
                  <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-hoop-accent-light text-hoop-accent">
                    National Team
                  </span>
                )}
              </p>
            )}
            {leagues.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {leagues.map((league, idx) => (
                  <span
                    key={idx}
                    className="px-2.5 py-1 rounded-md bg-hoop-bg text-sm text-hoop-text border border-hoop-border"
                  >
                    {league.league_name} ({league.season})
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Roster grid reusing PlayerCard */}
      <section className="bg-hoop-surface border border-hoop-border rounded-2xl p-6">
        <h2 className="text-sm font-semibold text-hoop-accent uppercase tracking-wider mb-4">
          Roster
        </h2>
        {rosterAsSearchResults.length === 0 ? (
          <div className="py-12 text-center text-hoop-text-2">
            <p>No players on roster yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {rosterAsSearchResults.map((player) => (
              <PlayerCard key={player.id} player={player} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
